import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, Plus, Trash2 } from 'lucide-react';
import { PaymentMethod } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { useCurrency } from '@/hooks/useCurrency';
import type { Tender } from '../api/sales.api';
import { methodNeedsReference } from '../tenderMethods';

interface TenderPanelProps {
  tenders: Tender[];
  totalMinor: number;
  outstandingMinor: number;
  onChange: (tenders: Tender[]) => void;
  /** Told whether an amount is typed but not yet added, for a screen that must not let it pass for a tender. The counter does not ask. */
  onPendingChange?: (pending: boolean) => void;
}

/**
 * Split tender.
 *
 * Several methods paying one checkout — cash, then a card, then a transfer. Not an instalment
 * plan: everything here settles now, and all of it commits with the sale or none of it does.
 */
export function TenderPanel({
  tenders,
  totalMinor,
  outstandingMinor,
  onChange,
  onPendingChange,
}: TenderPanelProps) {
  const { t } = useTranslation();
  const { format, parse, toInput } = useCurrency();

  const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onPendingChange?.(amount.trim() !== '');
  }, [amount, onPendingChange]);

  const addTender = () => {
    setError(null);

    const amountMinor = parse(amount);
    if (amountMinor === null || amountMinor <= 0) {
      setError(t('sales.validation.amountInvalid'));
      return;
    }
    if (amountMinor > outstandingMinor) {
      setError(t('sales.validation.overTendered'));
      return;
    }

    onChange([
      ...tenders,
      { method, amountMinor, ...(reference.trim() ? { reference: reference.trim() } : {}) },
    ]);
    setAmount('');
    setReference('');
  };

  return (
    <Card>
      <CardHeader
        title={t('sales.tender.title')}
        subtitle={t('sales.tender.subtitle')}
        action={
          <span className="text-muted font-mono text-xs" dir="ltr">
            {format(outstandingMinor)}
          </span>
        }
      />

      <div className="space-y-3 p-4 pt-0">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {tenders.length > 0 ? (
          <ul className="divide-line divide-y">
            {tenders.map((tender, index) => (
              <li
                key={`${tender.method}-${index}`}
                className="flex items-center justify-between gap-3 py-2"
              >
                <span className="min-w-0">
                  <span className="text-ink block text-sm font-medium">
                    {t(`sales.methods.${tender.method}`)}
                  </span>
                  {tender.reference ? (
                    <span className="text-muted font-mono text-xs" dir="ltr">
                      {tender.reference}
                    </span>
                  ) : null}
                </span>

                <span className="flex items-center gap-2">
                  <span className="text-ink font-medium" dir="ltr">
                    {format(tender.amountMinor)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('sales.tender.remove', {
                      method: t(`sales.methods.${tender.method}`),
                    })}
                    onClick={() => onChange(tenders.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {outstandingMinor > 0 ? (
          <div className="space-y-2">
            <FormField label={t('sales.fields.method')}>
              {({ id }) => (
                <Select
                  id={id}
                  options={Object.values(PaymentMethod).map((value) => ({
                    value,
                    label: t(`sales.methods.${value}`),
                  }))}
                  value={method}
                  onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                />
              )}
            </FormField>

            <Input
              label={t('sales.fields.amount')}
              inputMode="decimal"
              value={amount}
              placeholder={toInput(outstandingMinor)}
              onChange={(event) => setAmount(event.target.value)}
            />

            {methodNeedsReference(method) ? (
              <Input
                label={t('sales.fields.reference')}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={addTender}>
                <Plus className="size-4" aria-hidden />
                {t('sales.tender.add')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAmount(toInput(outstandingMinor))}
              >
                <Banknote className="size-4" aria-hidden />
                {t('sales.tender.exact')}
              </Button>
            </div>
          </div>
        ) : totalMinor > 0 ? (
          <Alert tone="success">{t('sales.tender.settled')}</Alert>
        ) : null}
      </div>
    </Card>
  );
}
